import type * as api from '@gitbook/api-client';

import { apiResource, usePaginatedAPIQuery } from '../../state';

/** Maximum number of conversations fetched for the list. */
const CONVERSATIONS_PAGE_SIZE = 100;

/** Stable empty list reference so consumers can safely use the result as effect/state input. */
const EMPTY_CONVERSATIONS: api.AgentConversation[] = [];

/**
 * Public id of the change request's implicit default conversation.
 *
 * The durable object behind it is addressed *without* a conversation id — it is the one the older
 * id-less agent used — but its index row has to be keyed by something, so it uses this sentinel.
 * A caller opening it must therefore address it with no conversation id, not with this value.
 * Must match `DEFAULT_AGENT_CONVERSATION_LOCAL_ID` in the backend database schema.
 */
export const DEFAULT_CONVERSATION_LOCAL_ID = 'default';

/**
 * Prefix of the id of an agent review conversation.
 *
 * Each review is its own conversation, belonging to whoever asked for it, so there is no known id
 * to open — the prefix is what identifies one among the conversations of a change request. Must
 * match `AGENT_REVIEW_CONVERSATION_LOCAL_ID_PREFIX` in the backend database schema.
 */
export const REVIEW_CONVERSATION_LOCAL_ID_PREFIX = 'review-';

/**
 * Whether a conversation is one of the agent's reviews of the change request.
 */
export function isReviewConversation(conversation: api.AgentConversation): boolean {
    return conversation.id.startsWith(REVIEW_CONVERSATION_LOCAL_ID_PREFIX);
}

/**
 * Public id of the conversation a style guide check runs in.
 *
 * Unlike a review, it is at a known id and shared, so every viewer of the change request opens the
 * same one. Must match `AGENT_STYLEGUIDE_CHECK_CONVERSATION_LOCAL_ID` in the backend schema.
 */
export const STYLEGUIDE_CHECK_CONVERSATION_LOCAL_ID = 'styleguide-check';

/**
 * Generate a new client-side conversation id. It addresses the conversation's durable object and
 * becomes its public id once the conversation is persisted on its first message. Random so it never
 * collides with the reserved default id or with the agent's own reviews.
 */
export function generateConversationLocalId(): string {
    return `c-${crypto.randomUUID()}`;
}

/**
 * Scope identifying the change request a conversation belongs to.
 */
export type ChangeRequestConversationScope = {
    spaceId: string;
    changeRequestId: string;
};

/**
 * List the agent conversations of a change request, most recently updated first.
 *
 * This does not include the default (legacy) conversation, which is surfaced separately
 * by the client and connects to the durable object without a conversation id.
 */
export function useChangeRequestConversations(
    scope: ChangeRequestConversationScope,
    options?: { skip?: boolean }
): {
    conversations: api.AgentConversation[];
    isLoading: boolean;
    refetch: () => Promise<unknown>;
} {
    const { data, refetch } = usePaginatedAPIQuery(
        apiResource('listChangeRequestConversations', {
            spaceId: scope.spaceId,
            changeRequestId: scope.changeRequestId,
            limit: CONVERSATIONS_PAGE_SIZE,
        }),
        { skip: options?.skip ?? false }
    );

    return {
        conversations: data?.items ?? EMPTY_CONVERSATIONS,
        isLoading: options?.skip ? false : !data,
        refetch,
    };
}
